import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { RETURN_REASONS } from './helpers';

const ReturnTable = ({
  groupedBoxRows, hasMultipleBoxes, rows,
  updateRow, selectDesign, removeRow, addRow, filteredDesigns, handleTableKeyDown,
  breakPopupIdx, setBreakPopupIdx, handleBreak, handleUndoBreak, currentBoxNumber,
  grandQty, grandAmount,
}) => (
  <Card>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="pr-invoice-table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left w-12 font-semibold text-gray-600">Sr</th>
              <th className="px-3 py-2 text-left min-w-[180px] font-semibold text-gray-600">Design No</th>
              <th className="px-3 py-2 text-left w-32 font-semibold text-gray-600">Last Bill #</th>
              <th className="px-3 py-2 text-left w-28 font-semibold text-gray-600">Bill Date</th>
              <th className="px-3 py-2 text-left min-w-[120px] font-semibold text-gray-600">Colour</th>
              <th className="px-3 py-2 text-left w-24 font-semibold text-gray-600">Size</th>
              <th className="px-3 py-2 text-right w-20 font-semibold text-gray-600">Ret Qty</th>
              <th className="px-3 py-2 text-right w-20 font-semibold text-gray-600">Rate</th>
              <th className="px-3 py-2 text-right w-24 font-semibold text-gray-600">Amount</th>
              <th className="px-3 py-2 text-left w-28 font-semibold text-gray-600">Reason</th>
              <th className="px-3 py-2 text-left w-28 font-semibold text-gray-600">Notes</th>
              <th className="px-3 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedBoxRows).sort(([a],[b]) => Number(a) - Number(b)).map(([boxNum, boxRows]) => (
              <React.Fragment key={`box-${boxNum}`}>
                {hasMultipleBoxes && (
                  <tr className="bg-amber-100 border-b-2 border-amber-300">
                    <td colSpan="12" className="px-3 py-2 font-bold text-amber-800 text-sm">Box {boxNum}</td>
                  </tr>
                )}
                {boxRows.map((row) => {
                  const idx = row._idx;
                  return (
                <tr key={idx} className="border-b hover:bg-gray-50/50" data-testid={`pr-row-${idx}`}>
                  <td className="px-3 py-1.5 text-gray-500 font-mono">{row.sr}</td>
                  <td className="px-3 py-1.5 relative" data-pr-design-cell>
                    <Input value={row.designSearch}
                      onChange={e => { updateRow(idx, 'designSearch', e.target.value); updateRow(idx, 'showDesignDropdown', true); }}
                      onFocus={() => updateRow(idx, 'showDesignDropdown', true)}
                      onClick={() => updateRow(idx, 'showDesignDropdown', true)}
                      placeholder="Search design..." className="h-8 text-sm" data-testid={`pr-design-search-${idx}`}
                      data-row={idx} data-col={0} onKeyDown={(e) => {
                        if ((e.key === 'Tab' || e.key === 'Enter') && row.designSearch && !row.product_id) {
                          const matches = filteredDesigns(row.designSearch);
                          const exact = matches.find(p => p.design_code.toLowerCase() === row.designSearch.toLowerCase());
                          const pick = exact || (matches.length === 1 ? matches[0] : null);
                          if (pick) { e.preventDefault(); selectDesign(idx, pick); return; }
                        }
                        handleTableKeyDown(e);
                      }}
                    />
                    {row.showDesignDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto left-0">
                        {filteredDesigns(row.designSearch).map(p => (
                          <div key={p.id} className="px-3 py-2 hover:bg-red-50 cursor-pointer text-sm flex items-center gap-2"
                            onMouseDown={(e) => { e.preventDefault(); selectDesign(idx, p); }} data-testid={`pr-design-option-${p.id}`}>
                            <span className="font-mono font-semibold">{p.design_code}</span>
                            <span className="text-gray-400 text-xs">{p.category}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="text-xs font-medium text-emerald-700" data-testid={`pr-bill-ref-${idx}`}>
                      {row.source_bill || <span className="text-gray-300">—</span>}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="text-xs text-gray-500" data-testid={`pr-bill-date-${idx}`}>
                      {row.source_bill_date ? (() => { const d = new Date(row.source_bill_date); return isNaN(d) ? row.source_bill_date : `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getFullYear()).slice(-2)}`; })() : <span className="text-gray-300">—</span>}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={row.color} onChange={e => updateRow(idx, 'color', e.target.value)}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      disabled={!row.product_id} data-testid={`pr-color-${idx}`}
                      data-row={idx} data-col={1} onKeyDown={handleTableKeyDown}>
                      <option value="">Select</option>
                      {(row.colors || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={row.size} onChange={e => updateRow(idx, 'size', e.target.value)}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      disabled={!row.color} data-testid={`pr-size-${idx}`}
                      data-row={idx} data-col={2} onKeyDown={handleTableKeyDown}>
                      <option value="">Select</option>
                      {(row.sizeGroups || []).length > 1 && (() => {
                        const allSizes = (row.sizeGroups || []).flatMap(g => g.sizes);
                        return <option value="both">{allSizes[0]} to {allSizes[allSizes.length - 1]} ({allSizes.length}pcs)</option>;
                      })()}
                      {(row.sizeGroups || []).length > 0 && <optgroup label="Sets">
                        {(row.sizeGroups || []).map(g => <option key={g.id} value={g.id}>{g.label} ({g.count}pcs)</option>)}
                      </optgroup>}
                      <optgroup label="Individual">
                        {(row.individualSizes || []).map(s => <option key={s.size} value={s.size}>{s.size}</option>)}
                      </optgroup>
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <Input type="number" min="0" value={row.qty} onChange={e => updateRow(idx, 'qty', e.target.value)}
                      className="h-8 text-right text-sm" onWheel={e => e.target.blur()} data-testid={`pr-qty-${idx}`}
                      data-row={idx} data-col={3} onKeyDown={handleTableKeyDown} />
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <span className="text-xs font-medium text-gray-600" data-testid={`pr-rate-${idx}`}>
                      {row.rate ? `₹${row.rate.toLocaleString('en-IN')}` : <span className="text-gray-300">—</span>}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {(() => {
                      const q = parseInt(row.qty) || 0;
                      const rate = row.rate || 0;
                      let amt = 0;
                      if (row.size === 'both') {
                        amt = (row.sizeGroups || []).reduce((sum, g) => sum + q * g.count * (g.rate || rate), 0);
                      } else {
                        const grp = (row.sizeGroups || []).find(g => g.id === row.size);
                        amt = grp ? q * grp.count * rate : q * rate;
                      }
                      return (
                        <span className={`text-xs font-semibold ${amt > 0 ? 'text-red-700' : 'text-gray-300'}`} data-testid={`pr-amount-${idx}`}>
                          {amt > 0 ? `₹${amt.toLocaleString('en-IN')}` : '—'}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={row.reason} onChange={e => updateRow(idx, 'reason', e.target.value)}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      data-testid={`pr-reason-${idx}`}
                      data-row={idx} data-col={4} onKeyDown={handleTableKeyDown}>
                      {RETURN_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <Input value={row.notes || ''} onChange={e => updateRow(idx, 'notes', e.target.value)}
                      placeholder="Optional" className="h-8 text-xs" data-testid={`pr-item-notes-${idx}`}
                      data-row={idx} data-col={5} onKeyDown={handleTableKeyDown} />
                  </td>
                  <td className="px-3 py-1.5 flex items-center gap-1">
                    <div className="relative">
                      <button onClick={() => setBreakPopupIdx(breakPopupIdx === idx ? null : idx)} className="text-amber-500 hover:text-amber-700 text-xs font-semibold border border-amber-300 rounded px-2 py-0.5 hover:bg-amber-50" data-testid={`pr-break-${idx}`}>Break</button>
                      {breakPopupIdx === idx && (
                        <div className="absolute right-0 top-7 z-50 bg-white border rounded-lg shadow-xl p-2 w-36">
                          <button onClick={handleBreak} className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 rounded" data-testid="pr-break-new-box">New Box</button>
                        </div>
                      )}
                    </div>
                    {currentBoxNumber > 1 && (
                      <button onClick={handleUndoBreak} className="text-blue-500 hover:text-blue-700 text-xs font-semibold border border-blue-300 rounded px-2 py-0.5 hover:bg-blue-50" data-testid={`pr-undo-break-${idx}`}>Undo</button>
                    )}
                    {rows.length > 1 && (
                      <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600" data-testid={`pr-remove-row-${idx}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </td>
                </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2">
            <tr>
              <td colSpan="6" className="px-3 py-2">
                <Button variant="outline" size="sm" onClick={addRow} className="text-xs" data-testid="pr-add-row">
                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add Row
                </Button>
              </td>
              <td className="px-3 py-2 text-right font-bold text-red-700 text-base" data-testid="pr-grand-qty">{grandQty}</td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2 text-right font-bold text-red-700 text-base" data-testid="pr-grand-amount">
                {grandAmount > 0 ? `₹${grandAmount.toLocaleString('en-IN')}` : ''}
              </td>
              <td colSpan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default ReturnTable;
